import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { ControlProps } from "@jsonforms/core";
import { useEffect, useState, useRef } from "react";
import { getAllStates } from "services";
import CustomSearchDropdown from "../../../../../../shadcn/components/custom/dropdown/CustomSearchDropdown";

const isPincodeBasedFetchingEnabled =
  process.env.NEXT_PUBLIC_ENABLE_PINCODE_BASED_FETCHING === "true";

// Title case helper - converts "maharashtra" to "Maharashtra"
const toTitleCase = (str: string): string => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

interface OptionProps {
  label?: string; // Display name
  value?: string; // Unique identifier
}
export const StateFieldTypeRenderer = ({
  data,
  handleChange,
  path,
  schema,
  uischema,
}: ControlProps) => {
  const ctx = useJsonForms();
  const dropDownOptions = uischema?.options?.additionalData || {};
  const [customError, setCustomError] = useState("");
  const [statesData, setStatesData] = useState<OptionProps[]>();
  const positionRef = useRef<HTMLInputElement>(null);

  const show = uischema?.options?.show;
  useEffect(() => {
    if (show == false) {
      handleChange(path, {});
    }
  }, [show]);

  useEffect(() => {
    const errorData = ctx?.core?.errors;
    let flag = false;
    errorData?.forEach((error: any) => {
      if (error?.instancePath === "/" + path) {
        setCustomError(error?.message);
        flag = true;
      } else if (
        error?.instancePath === "" &&
        error?.params?.missingProperty === path
      ) {
        setCustomError("Required Field");
        flag = true;
        return;
      }
    });

    if (!flag) setCustomError("");
    else {
      if (positionRef.current) {
        // const { top } = positionRef?.current?.getBoundingClientRect();
        // const scrollTop = window.scrollY || document.documentElement.scrollTop;
        // window.scrollTo({
        //   top: top + scrollTop - 20, // Component's top position relative to the document
        //   behavior: "smooth", // Smooth scrolling
        // });
        positionRef.current.scrollIntoView({
          behavior: "smooth",
          // block: "center",
        });
      }
    }
  }, [ctx?.core?.errors]);

  // Only fetch states when using dropdown (flag is OFF)
  useEffect(() => {
    if (isPincodeBasedFetchingEnabled) return; // Skip fetching when using inputs

    const fetch = async () => {
      try {
        const response: any = await getAllStates();
        if (response) {
          const fileteredData = response.map((state: any) => {
            return {
              uuid: state.uuid,
              label: state.name,
              value: state.slug,
            };
          });
          setStatesData(fileteredData);
        }
      } catch (err) {
        console.log("error here", err);
      }
    };
    fetch();
  }, []);

  // When feature flag is ON, show readonly text input with prefilled value from pincode
  const getDisplayValue = () => {
    if (!data) return "";
    // data can be { label, value, uuid } or { name } (for raw values)
    // Keep label as-is from API/DB, only title case raw 'name' values
    if (data.name) return toTitleCase(data.name); // Raw value from pincode - apply title case
    if (data.label) return data.label; // Keep label as-is from API
    return "";
  };

  if (show === false) {
    return <></>;
  }

  // When feature flag is ON, render as readonly input (prefilled from pincode)
  if (isPincodeBasedFetchingEnabled) {
    const displayValue = getDisplayValue();
    return (
      <div ref={positionRef} className="w-full relative">
        <div className="h-[50px] bg-gray-100 border border-gray-300 rounded">
          <div className="h-full flex items-center px-4">
            <span className="text-brand-textDark font-normal text-web-body-sm">
              {displayValue || schema?.title}
            </span>
          </div>
        </div>
        <div
          className={`absolute top-1 text-[10px] transition-all duration-300 left-3 ${
            displayValue ? "text-brand-borderGrey" : "text-brand-borderGrey"
          }`}
        >
          {schema?.title}
        </div>
        {customError && (
          <label
            htmlFor="name"
            className={"text-brand-textRed text-web-body-sm ml-2"}
          >
            {customError}
          </label>
        )}
      </div>
    );
  }

  // When feature flag is OFF, render as dropdown (old behavior)
  return (
    <div ref={positionRef} className="w-full relative">
      <div className="h-[50px] bg-white border  ">
        <CustomSearchDropdown
          onValueChange={(value: any) => {
            handleChange(path, value);
          }}
          placeholderText=""
          defaultSelected={
            data !== ""
              ? statesData?.find((item: any) => data?.value === item.value)
              : undefined
          }
          dataSource={() => {
            return Promise.resolve(statesData || []);
          }}
          styles={{
            placeholderClass: `pr-1 ${
              data ? "pt-4" : "pt-6"
            } text-brand-textLightGrey overflow-hidden`,
            triggerOuterClass: "h-full",
            formfieldClass: "h-full",
          }}
        />
        <div
          className={`absolute top-1 text-[10px] transition-all duration-300  left-3 ${"text-brand-borderGrey"}`}
        >
          {schema?.title}
        </div>
      </div>
      {customError && (
        <label
          htmlFor="name"
          className={"text-brand-textRed text-web-body-sm ml-2"}
        >
          {customError}
        </label>
      )}
    </div>
  );
};

export default withJsonFormsControlProps(StateFieldTypeRenderer);
